import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { registerKiosk } from '../../services/kioskAdminService';
import { getLocations } from '../../services/locationService';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import { kioskAdminErrorMessage } from '../../utils/kioskErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'KioskCreate'>;

export function KioskCreateScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [kioskName, setKioskName] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationLabel, setLocationLabel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const themed = useMemo(
    () => ({
      caption: { ...typography.caption, color: palette.textMuted },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const loadLocations = useCallback(async (search: string) => {
    const result = await getLocations({
      page: 1,
      pageSize: 20,
      filters: activeConfigurationFilters({ search: search || undefined }),
    });
    return result.items.map(
      (item): SelectorOption => ({
        id: item.locationId,
        label: item.locationName,
        subtitle: item.locationCode,
      }),
    );
  }, []);

  async function submit() {
    const name = kioskName.trim();
    if (!name) {
      setError('Kiosk name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await runOperation('add', () => registerKiosk({
        kioskName: name,
        deviceName: deviceName.trim() || null,
        locationId,
      }));
      navigation.replace(routes.kioskDetails, { kioskId: created.kioskId });
    } catch (caught) {
      setError(kioskAdminErrorMessage(caught, 'Unable to register kiosk.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll>
        <ScreenHeader
          title="Register kiosk"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <AppCard style={styles.card}>
            <AppInput
              label="Kiosk name"
              value={kioskName}
              onChangeText={setKioskName}
              placeholder="Lobby entrance"
              autoCapitalize="words"
            />
            <AppInput
              label="Device name (optional)"
              value={deviceName}
              onChangeText={setDeviceName}
              placeholder="Front desk tablet"
              autoCapitalize="words"
            />
            <SearchableEntitySelector
              label="Location (optional)"
              selectedValue={locationId}
              selectedLabel={locationLabel}
              loadOptions={loadLocations}
              onSelect={option => {
                setLocationId(option.id);
                setLocationLabel(option.label);
              }}
              onClear={() => {
                setLocationId(null);
                setLocationLabel(null);
              }}
              placeholder="Search locations"
            />
            <Text style={themed.caption}>
              Device identifier is bound when the kiosk app activates — do not
              put API secrets in the mobile app.
            </Text>
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
          </AppCard>
          <AppButton
            title={saving ? 'Registering…' : 'Register kiosk'}
            onPress={submit}
            disabled={saving}
          />
          <AppButton
            title="Cancel"
            variant="secondary"
            onPress={() => navigation.goBack()}
            disabled={saving}
          />
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md },
  card: { gap: spacing.md },
});
